import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';
export const wrapObs = (wrapper) => {
    return (source) => new Observable((observer) => source.subscribe({
        next: (x) => wrapper(() => observer.next(x)),
        error: (e) => wrapper(() => observer.error(e)),
        complete: () => wrapper(() => observer.complete()),
    }));
};
export class MonitoredSubject {
    subscribersSubj = new BehaviorSubject([]);
    get valueObs() {
        return new Observable((subscriber) => {
            this.subscribersSubj.next([...this.subscribersSubj.value, subscriber]);
            return {
                unsubscribe: () => {
                    this.subscribersSubj.next(this.subscribersSubj.value.filter((s) => s !== subscriber));
                },
            };
        });
    }
    get subscribers() {
        return this.subscribersSubj.value;
    }
    get subscribersObs() {
        return this.subscribersSubj.asObservable();
    }
    get subscriberCount() {
        return this.subscribersSubj.value.length;
    }
    get subscriberCountObs() {
        return this.subscribersSubj.pipe(map((a) => a.length));
    }
    get isSubscribed() {
        return this.subscriberCount > 0;
    }
    get isSubscribedObs() {
        return this.subscriberCountObs.pipe(map((n) => n > 0));
    }
    next(v) {
        for (const sub of this.subscribersSubj.value) {
            sub.next(v);
        }
    }
    error(e) {
        for (const sub of this.subscribersSubj.value) {
            sub.error(e);
        }
        this.subscribersSubj.next([]);
    }
    complete() {
        for (const sub of this.subscribersSubj.value) {
            sub.complete();
        }
        this.subscribersSubj.next([]);
    }
}
export class MonitoredBehaviorSubject extends MonitoredSubject {
    valueInt;
    constructor(valueInt) {
        super();
        this.valueInt = valueInt;
    }
    get value() {
        return this.valueInt;
    }
    get valueObs() {
        return new Observable((subscriber) => {
            subscriber.next(this.valueInt);
            return super.valueObs.subscribe(subscriber);
        });
    }
    next(v) {
        this.valueInt = v;
        super.next(v);
    }
}
/**
 * Allows multiple consumers to subscribe to an event source that requires async calls to start and stop.
 * Since Rx does not provide for async unsubscribe calls or ones that throw, info about the progress of stops
 * is provided via `stopErrors`.
 */
export class AsyncMulticastSubject {
    source;
    eventSubj = new MonitoredSubject();
    stopErrorsSubj = new Subject();
    stateSubj = new BehaviorSubject('stopped');
    replay;
    /**
     * @param source Async functions to start and stop the source of events
     * @param replay If true, once the source has been started, the last value emitted will be saved and
     * provided to consumers that subscribe later. Use this when the source `Observable` is hot and you
     * want it to appear as hot to all consumers.
     */
    constructor(source) {
        this.source = source;
        const subscribedChanged = this.eventSubj.isSubscribedObs.pipe(distinctUntilChanged(), skip(1));
        // Handle subscription changes when the source is stable (no start or stop is ongoing)
        subscribedChanged
            .pipe(filter((v) => v && this.stateSubj.value === 'stopped'))
            .subscribe(() => void this.enterStarting());
        subscribedChanged
            .pipe(filter((v) => !v && this.stateSubj.value === 'started'))
            .subscribe(this.enterStopping);
        subscribedChanged
            .pipe(filter((v) => !v && this.stateSubj.value === 'waitUnsubscribe'))
            .subscribe(() => this.stateSubj.next('stopped'));
        // If we finish a start or stop transition and the subscription state is discordant, fix it
        this.stateSubj
            .pipe(filter((state) => state === 'stopped' && this.eventSubj.isSubscribed))
            .subscribe(() => void this.enterStarting());
        this.stateSubj
            .pipe(filter((state) => state === 'started' && !this.eventSubj.isSubscribed))
            .subscribe(this.enterStopping);
    }
    get state() {
        return this.stateSubj.value;
    }
    get stateObs() {
        return this.stateSubj.asObservable();
    }
    get stopErrors() {
        return this.stopErrorsSubj.asObservable();
    }
    get = () => new Promise((resolve, reject) => {
        // eslint-disable-next-line prefer-const
        let eventSub;
        let isPromiseCompleted = false;
        let subscriberBuffer;
        const makeObservable = () => new Observable((subscriber) => {
            switch (subscriberBuffer?.type) {
                case 'subscribed':
                    throw new Error('Logic error: already subscribed');
                case 'error':
                    subscriber.error(subscriberBuffer.error);
                    break;
                case 'complete':
                    subscriber.complete();
                    break;
                case undefined:
                    subscriberBuffer = { type: 'subscribed', subscriber };
                    if (this.replay?.type === 'every' || this.replay?.type === 'first') {
                        subscriber.next(this.replay.value);
                    }
                    if (this.replay?.type === 'first') {
                        this.replay = { type: 'noMore' };
                    }
                    break;
            }
            return () => {
                eventSub?.unsubscribe();
            };
        });
        eventSub = this.eventSubj.valueObs.subscribe((v) => {
            switch (v.type) {
                case 'started':
                    if (!isPromiseCompleted) {
                        resolve(makeObservable());
                        isPromiseCompleted = true;
                    }
                    break;
                case 'startFailed':
                    eventSub?.unsubscribe();
                    this.stateSubj.next('stopped');
                    if (!isPromiseCompleted) {
                        reject(v.err);
                        isPromiseCompleted = true;
                    }
                    break;
                case 'next':
                    if (subscriberBuffer?.type === 'subscribed') {
                        subscriberBuffer.subscriber.next(v.value);
                    }
                    break;
                case 'error':
                    this.stateSubj.next('waitUnsubscribe');
                    if (!subscriberBuffer) {
                        subscriberBuffer = { type: 'error', error: v.err };
                    }
                    else if (subscriberBuffer.type === 'subscribed') {
                        subscriberBuffer.subscriber.error(v.err);
                    }
                    break;
                case 'complete':
                    this.stateSubj.next('waitUnsubscribe');
                    if (!subscriberBuffer) {
                        subscriberBuffer = { type: 'complete' };
                    }
                    else if (subscriberBuffer.type === 'subscribed') {
                        subscriberBuffer.subscriber.complete();
                    }
                    break;
            }
        });
        if (this.stateSubj.value === 'started') {
            isPromiseCompleted = true;
            resolve(makeObservable());
        }
    });
    enterStarting = async () => {
        this.stateSubj.next('starting');
        try {
            const obs = await this.source.start();
            this.eventSubj.next({ type: 'started' });
            obs.subscribe({
                next: (value) => {
                    if (this.source.replay) {
                        this.replay = { type: 'every', value };
                    }
                    else if (!this.replay) {
                        // Not replaying to all, but no consumers are listening yet, so save the first value
                        this.replay = { type: 'first', value };
                    }
                    this.eventSubj.next({ type: 'next', value });
                },
                error: (err) => this.eventSubj.next({ type: 'error', err }),
                complete: () => this.eventSubj.next({ type: 'complete' }),
            });
            this.stateSubj.next('started');
        }
        catch (err) {
            this.eventSubj.next({ type: 'startFailed', err });
            this.stateSubj.next('stopped');
        }
    };
    enterStopping = () => {
        this.stateSubj.next('stopping');
        this.replay = undefined;
        this.source
            .stop()
            .then(() => {
            this.stateSubj.next('stopped');
        })
            .catch((err) => {
            this.stopErrorsSubj.next(err);
            this.stateSubj.next('stopped');
        });
    };
}
export const toBehaviorSubject = (source) => {
    let subject;
    return new Promise((resolve, reject) => {
        let synchronousValue;
        let subscription;
        // eslint-disable-next-line prefer-const
        subscription = source.subscribe({
            next: (value) => {
                if (subject) {
                    subject.next(value);
                }
                else {
                    subject = new BehaviorSubject(value);
                    if (subscription) {
                        resolve({ subject, subscription });
                    }
                    else {
                        synchronousValue = { value, subject };
                    }
                }
            },
            error: (err) => {
                if (subject) {
                    subject.error(err);
                }
                else {
                    reject(err);
                }
            },
            complete: () => {
                if (subject) {
                    subject.complete();
                }
                else {
                    resolve(undefined);
                }
            },
        });
        if (synchronousValue) {
            resolve({ subject: synchronousValue.subject, subscription });
            synchronousValue = undefined; // clear it so it can be GCed
        }
    });
};
//# sourceMappingURL=rx.js.map